import type { RunContext } from '../run-context';
import type { AgentInputItem } from '../types';
import { formatTaskList } from './format';
import type { TaskListStore } from './types';

export const TASK_REMINDER_MARKER = 'task-list-reminder';

const TASK_MANAGEMENT_TOOLS = new Set(['TaskCreate', 'TaskUpdate']);
const DEFAULT_REMINDER_TURN_INTERVAL = 10;
const TASK_REMINDER_STATE = Symbol('openai.agents.task_list.reminder_state');

type TaskReminderRuntimeState = {
  lastReminderTurn?: number;
};

export type TaskReminderOptions = {
  input: AgentInputItem[];
  store: TaskListStore;
  taskListId: string;
  runContext?: RunContext<any>;
  currentTurn?: number;
  interval?: number;
};

export async function maybeAppendTaskReminder(
  options: TaskReminderOptions,
): Promise<AgentInputItem[]> {
  const interval = options.interval ?? DEFAULT_REMINDER_TURN_INTERVAL;
  const tasks = await options.store.transaction(options.taskListId, (state) =>
    state.list(),
  );
  if (tasks.length === 0) {
    return options.input;
  }

  const currentTurn = options.currentTurn ?? 0;
  const runtimeState = options.runContext?._getEphemeralSharedState(
    TASK_REMINDER_STATE,
    (): TaskReminderRuntimeState => ({}),
  );
  if (
    runtimeState?.lastReminderTurn !== undefined &&
    currentTurn - runtimeState.lastReminderTurn < interval
  ) {
    return options.input;
  }

  const history = scanTaskReminderHistory(options.input);
  if (
    history.assistantTurns - history.lastTaskManagementAssistantTurn <
    interval
  ) {
    return options.input;
  }
  if (history.assistantTurns - history.lastReminderAssistantTurn < interval) {
    return options.input;
  }

  if (runtimeState) {
    runtimeState.lastReminderTurn = currentTurn;
  }

  return [
    ...options.input,
    {
      type: 'message',
      role: 'user',
      content: renderTaskReminder(formatTaskList(tasks)),
    },
  ];
}

export function renderTaskReminder(taskList: string): string {
  return [
    '<system-reminder>',
    `<!-- ${TASK_REMINDER_MARKER} -->`,
    '任务工具近期未被使用。如果任务状态已发生变化，请考虑使用 TaskCreate 或 TaskUpdate。如果此提醒不适用，请忽略，且不要向用户提及。',
    '',
    '当前任务清单:',
    taskList,
    '</system-reminder>',
  ].join('\n');
}

function scanTaskReminderHistory(input: AgentInputItem[]): {
  assistantTurns: number;
  lastTaskManagementAssistantTurn: number;
  lastReminderAssistantTurn: number;
} {
  let assistantTurns = 0;
  let lastTaskManagementAssistantTurn = 0;
  let lastReminderAssistantTurn = 0;

  for (const item of input) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const role = (item as { role?: unknown }).role;
    if (role === 'assistant') {
      assistantTurns++;
    }

    const type = (item as { type?: unknown }).type;
    const name = (item as { name?: unknown }).name;
    if (type === 'function_call' && typeof name === 'string') {
      if (TASK_MANAGEMENT_TOOLS.has(name)) {
        lastTaskManagementAssistantTurn = assistantTurns;
      }
    }

    if (role === 'user' && contentIncludesReminderMarker(item)) {
      lastReminderAssistantTurn = assistantTurns;
    }
  }

  return {
    assistantTurns,
    lastTaskManagementAssistantTurn,
    lastReminderAssistantTurn,
  };
}

function contentIncludesReminderMarker(item: AgentInputItem): boolean {
  const content = (item as { content?: unknown }).content;
  if (typeof content === 'string') {
    return content.includes(TASK_REMINDER_MARKER);
  }
  if (!Array.isArray(content)) {
    return false;
  }
  return content.some(
    (part) =>
      part &&
      typeof part === 'object' &&
      (part as { type?: unknown }).type === 'input_text' &&
      typeof (part as { text?: unknown }).text === 'string' &&
      (part as { text: string }).text.includes(TASK_REMINDER_MARKER),
  );
}
